import React, { useState, useMemo } from 'react';
import { 
  Code2, 
  Cpu, 
  Database, 
  TrendingUp, 
  Layers, 
  ShieldCheck, 
  Sparkles,
  RotateCw
} from 'lucide-react';

export interface CircularTechRingsProps {
  activeCategory?: string;
  onSelectCategory?: (category: string) => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  viewMode?: "focused" | "all";
  onViewModeChange?: (mode: "focused" | "all") => void;
  showCenterHub?: boolean;
  className?: string;
}

export interface RingConfig {
  id: string;
  number: number;
  title: string;
  radius: number;
  speed: number;
  direction: "cw" | "ccw";
  strokeColor: string;
  textColor: string;
  fontSize: number;
  letterSpacing: string;
  fontWeight: "bold" | "600" | "700";
  icon: React.ElementType;
  tools: string[];
}

// 6 Concentric Rings with ALL 110+ Production Tools mapped into curvy rotating lines
export const concentricRingsConfig: RingConfig[] = [
  {
    id: "ring-frontend",
    number: 1,
    title: "Frontend & UI Architecture",
    radius: 440,
    speed: 130, // seconds for 360 deg
    direction: "cw",
    strokeColor: "#d8aa68",
    textColor: "#ffe0a3",
    fontSize: 12.5,
    letterSpacing: "0.18em",
    fontWeight: "700",
    icon: Code2,
    tools: [
      "ADMIN DASHBOARDS", "LOGIN PORTALS", "REACT.JS", "NEXT.JS 15", "TYPESCRIPT", "TAILWIND CSS", 
      "REACT NATIVE", "MOBILE APPS", "JAVASCRIPT ES6+", "REDUX TOOLKIT", "VITE", "HTML5 / CSS3", 
      "ANT DESIGN", "MATERIAL UI", "SHADCN UI", "FRAMER MOTION", "SVELTE", "RESPONSIVE UI", 
      "WCAG 2.1 AA", "ZUSTAND", "REACT QUERY", "BOOTSTRAP", "SASS / SCSS", "STORYBOOK"
    ]
  },
  {
    id: "ring-backend",
    number: 2,
    title: "Backend, APIs & Cloud DBs",
    radius: 382,
    speed: 110,
    direction: "ccw",
    strokeColor: "#caa062",
    textColor: "#f7d598",
    fontSize: 12,
    letterSpacing: "0.17em",
    fontWeight: "600",
    icon: Database,
    tools: [
      "PYTHON 3.12", "FASTAPI", "DJANGO", "NODE.JS", "EXPRESS.JS", 
      "SUPABASE", "POSTGRESQL", "MONGODB", "FIREBASE FIRESTORE", "RESTFUL APIS", 
      "GRAPHQL", "REDIS CACHE", "PRISMA ORM", "SQLITE", "DYNAMODB", 
      "WEBSOCKETS", "MICROSERVICES", "TRPC", "KAFKA", "ELASTICSEARCH"
    ]
  },
  {
    id: "ring-ai",
    number: 3,
    title: "AI Models, LLMs & Automation",
    radius: 324,
    speed: 90,
    direction: "cw",
    strokeColor: "#e6ba76",
    textColor: "#ffe5b3",
    fontSize: 11.5,
    letterSpacing: "0.16em",
    fontWeight: "700",
    icon: Cpu,
    tools: [
      "N8N AUTOMATION", "CLAUDE 3.7", "OPENAI API", "CHATGPT", "DEEPSEEK R1", 
      "LANGCHAIN", "AGENTIC AI", "RAG PIPELINES", "PINECONE VECTOR DB", "PROMPT OPS", 
      "LLM EMBEDDINGS", "MAKE.COM", "CRM WORKFLOWS", "AUTONOMOUS AGENTS", "GEMINI 2.0", 
      "OLLAMA", "HUGGING FACE", "FUNCTION CALLING"
    ]
  },
  {
    id: "ring-ads",
    number: 4,
    title: "Paid Media, Ads & ROAS Optimization",
    radius: 266,
    speed: 75,
    direction: "ccw",
    strokeColor: "#d8aa68",
    textColor: "#f3cc88",
    fontSize: 11,
    letterSpacing: "0.15em",
    fontWeight: "600",
    icon: TrendingUp,
    tools: [
      "META ADS MANAGER", "FACEBOOK ADS", "INSTAGRAM ADS", "GOOGLE ADS SEARCH", 
      "PERFORMANCE MAX (PMAX)", "YOUTUBE ADS", "TIKTOK ADS", "META PIXEL & CAPI", 
      "GOOGLE TAG MANAGER", "ROAS SCALING", "RETARGETING FUNNELS", "GA4 TELEMETRY", 
      "KLAVIYO", "CONVERSION OPT", "A/B SPLIT TESTING", "FUNNEL DESIGN"
    ]
  },
  {
    id: "ring-design",
    number: 5,
    title: "Product Design & Design Systems",
    radius: 208,
    speed: 60,
    direction: "cw",
    strokeColor: "#be904f",
    textColor: "#ffdca3",
    fontSize: 10.5,
    letterSpacing: "0.14em",
    fontWeight: "700",
    icon: Layers,
    tools: [
      "FIGMA", "FRAMER", "ADOBE XD", "ADOBE CREATIVE SUITE", "WEBFLOW", 
      "FLUTTERFLOW", "DESIGN TOKENS", "UX WIREFRAMING", "RAPID PROTOTYPING", 
      "BRAND IDENTITY SYSTEMS", "SKETCH", "INTERACTION DESIGN", "CANVA PRO", "TYPOGRAPHY OPS"
    ]
  },
  {
    id: "ring-devops",
    number: 6,
    title: "DevOps, Cloud, Security & QA",
    radius: 150,
    speed: 45,
    direction: "ccw",
    strokeColor: "#caa062",
    textColor: "#ffe0a3",
    fontSize: 10,
    letterSpacing: "0.13em",
    fontWeight: "700",
    icon: ShieldCheck,
    tools: [
      "ENTERPRISE SECURITY", "ZERO-TRUST SEC", "RBAC PERMISSIONS", "DOCKER", "AWS CLOUD", 
      "GIT", "GITHUB ACTIONS", "CI/CD PIPELINES", "CLOUDFLARE CDN", "VERCEL", "NETLIFY", 
      "SSL / TLS", "JWT AUTH", "OAUTH 2.0", "STRIPE PAYMENTS", "TWILIO", "CYPRESS", "JEST TESTS", 
      "VITEST", "LIGHTHOUSE 100", "API GATEWAY"
    ]
  }
];

export const CircularTechRings: React.FC<CircularTechRingsProps> = ({
  activeCategory = "Frontend",
  onSelectCategory,
  searchQuery = "",
  onSearchChange,
  viewMode = "all",
  onViewModeChange,
  showCenterHub = true,
  className = ""
}) => {
  const [isRotating, setIsRotating] = useState(true);
  const [hoveredRingId, setHoveredRingId] = useState<string | null>(null);

  // Precision 1000 x 1000 Canvas Center
  const CX = 500;
  const CY = 500;
  const R_HOUSING = 485;
  const R_CORE = 104;

  // Calculate total tools across all rings (110+ tools!)
  const totalToolsCount = useMemo(() => {
    return concentricRingsConfig.reduce((acc, r) => acc + r.tools.length, 0);
  }, []);

  // Filter tools if search query is provided
  const searchMatches = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase().trim();
    const matches: Array<{ ringId: string; tool: string }> = [];
    concentricRingsConfig.forEach((ring) => {
      ring.tools.forEach((t) => {
        if (t.toLowerCase().includes(q)) {
          matches.push({ ringId: ring.id, tool: t });
        }
      });
    });
    return matches;
  }, [searchQuery]);

  // Outer bezel ticks
  const bezelTicks = useMemo(() => {
    return Array.from({ length: 120 }).map((_, i) => {
      const angle = (i * 3 * Math.PI) / 180;
      const isMajor = i % 10 === 0;
      const isSemi = i % 5 === 0;
      const len = isMajor ? 14 : isSemi ? 8 : 4;
      return {
        x1: CX + (R_HOUSING - len) * Math.cos(angle),
        y1: CY + (R_HOUSING - len) * Math.sin(angle),
        x2: CX + R_HOUSING * Math.cos(angle),
        y2: CY + R_HOUSING * Math.sin(angle),
        isMajor,
        isSemi
      };
    });
  }, [CX, CY, R_HOUSING]);

  // Find active or hovered ring details for center display
  const activeHoveredRing = useMemo(() => {
    if (hoveredRingId) {
      return concentricRingsConfig.find(r => r.id === hoveredRingId);
    }
    return null;
  }, [hoveredRingId]);

  return (
    <div className={`relative w-full max-w-[940px] mx-auto flex flex-col items-center select-none ${className}`}>
      
      {/* Dynamic Keyframes for smooth continuous rotation */}
      <style>{`
        @keyframes orbit-spin-cw {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes orbit-spin-ccw {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }
        .anim-spin-cw {
          animation-name: orbit-spin-cw;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
          will-change: transform;
        }
        .anim-spin-ccw {
          animation-name: orbit-spin-ccw;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
          will-change: transform;
        }
      `}</style>

      {/* Ambient Warm Radial Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[680px] h-[680px] rounded-full bg-[radial-gradient(circle_at_center,rgba(216,170,104,0.14)_0%,rgba(77,39,20,0.06)_55%,transparent_75%)] pointer-events-none -z-10" />

      {/* =========================================================================
          MASTER SVG CANVASS: 6 CONCENTRIC ROTATING CURVY LINES WITH 110+ TOOLS
         ========================================================================= */}
      <div className="relative w-full aspect-square max-w-[900px] drop-shadow-[0_24px_70px_rgba(0,0,0,0.98)]">
        <svg
          viewBox="0 0 1000 1000"
          className="w-full h-full overflow-visible"
        >
          <defs>
            {/* Define the 360-degree continuous circular paths for each concentric ring */}
            {concentricRingsConfig.map((ring) => {
              const r = ring.radius;
              // Continuous circular path starting at top 12 o'clock and circling around
              return (
                <path
                  key={`def-path-${ring.id}`}
                  id={`curve-path-${ring.id}`}
                  d={`M ${CX} ${CY - r} A ${r} ${r} 0 1 1 ${CX - 0.001} ${CY - r}`}
                />
              );
            })}

            {/* Radial Gradient for Center Radar Hub */}
            <radialGradient id="masterHubGrad" cx="45%" cy="38%" r="65%">
              <stop offset="0%" stopColor="#4d2411" />
              <stop offset="45%" stopColor="#2c1409" />
              <stop offset="85%" stopColor="#140702" />
              <stop offset="100%" stopColor="#080201" />
            </radialGradient>

            {/* Gold Bezel Gradient */}
            <linearGradient id="goldBezelGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ffe5a3" />
              <stop offset="50%" stopColor="#d8aa68" />
              <stop offset="100%" stopColor="#8c5822" />
            </linearGradient>

            {/* Circular Clip Path for Central Pelican Logo Emblem */}
            <clipPath id="centerLogoClip">
              <circle cx={CX} cy={CY} r={R_CORE - 4} />
            </clipPath>
          </defs>

          {/* =========================================================================
              1. STATIC OUTER BEZEL HOUSING & PRECISION HOROLOGY TICKS
             ========================================================================= */}
          <circle
            cx={CX}
            cy={CY}
            r={R_HOUSING}
            fill="#090503"
            stroke="#261208"
            strokeWidth="4"
          />

          {/* Outer Graduation Ticks */}
          <g opacity="0.85">
            {bezelTicks.map((t, idx) => (
              <line
                key={`outer-tick-${idx}`}
                x1={t.x1}
                y1={t.y1}
                x2={t.x2}
                y2={t.y2}
                stroke={t.isMajor ? "#ffe0a3" : t.isSemi ? "#d8aa68" : "#6e3f19"}
                strokeWidth={t.isMajor ? 2.5 : t.isSemi ? 1.5 : 0.8}
                opacity={t.isMajor ? 1 : t.isSemi ? 0.75 : 0.35}
              />
            ))}
          </g>

          {/* 4 Cardinal Brass Screws */}
          {[
            { x: CX + R_HOUSING - 3, y: CY },
            { x: CX - R_HOUSING + 3, y: CY },
            { x: CX, y: CY + R_HOUSING - 3 },
            { x: CX, y: CY - R_HOUSING + 3 }
          ].map((sc, i) => (
            <g key={`screw-${i}`} transform={`translate(${sc.x}, ${sc.y})`}>
              <circle r="5" fill="url(#goldBezelGrad)" stroke="#1a0a03" strokeWidth="1" />
              <line x1="-3" y1="0" x2="3" y2="0" stroke="#1a0a03" strokeWidth="0.9" />
            </g>
          ))}

          {/* =========================================================================
              2. THE 6 CONCENTRIC ROTATING CURVY LINES (EACH LINE CARRYING TOOLS CURVED)
             ========================================================================= */}
          {concentricRingsConfig.map((ring) => {
            const isHovered = hoveredRingId === ring.id;
            const hasMatch = searchMatches.some(m => m.ringId === ring.id);
            const isHighlighted = isHovered || (searchQuery.trim() !== "" && hasMatch);
            const isFaded = searchQuery.trim() !== "" && !hasMatch;

            // Prepare the tool text string joined with sparkling bullet glyphs
            const fullToolsString = ring.tools.join("  ✦  ") + "  ✦  ";
            const circumference = 2 * Math.PI * ring.radius;

            return (
              <g
                key={ring.id}
                className="transition-opacity duration-300 cursor-pointer"
                opacity={isFaded ? 0.25 : 1}
                onMouseEnter={() => setHoveredRingId(ring.id)}
                onMouseLeave={() => setHoveredRingId(null)}
              >
                {/* 1. Underlying Concentric Circular Guideline Track */}
                <circle
                  cx={CX}
                  cy={CY}
                  r={ring.radius}
                  fill="none"
                  stroke={isHighlighted ? "#ffe0a3" : ring.strokeColor}
                  strokeWidth={isHighlighted ? 2.5 : 1.2}
                  strokeDasharray={isHighlighted ? "none" : "3 5"}
                  strokeOpacity={isHighlighted ? 0.9 : 0.4}
                  className="transition-all duration-300"
                />

                {/* 2. Secondary Faint Inner Guide Ring */}
                <circle
                  cx={CX}
                  cy={CY}
                  r={ring.radius - 8}
                  fill="none"
                  stroke={ring.strokeColor}
                  strokeWidth="0.5"
                  strokeDasharray="2 4"
                  strokeOpacity="0.15"
                />

                {/* 3. ROTATING CURVY TEXT GROUP */}
                <g
                  className={ring.direction === "cw" ? "anim-spin-cw origin-center" : "anim-spin-ccw origin-center"}
                  style={{
                    transformOrigin: `${CX}px ${CY}px`,
                    animationDuration: `${ring.speed}s`,
                    animationPlayState: isRotating ? "running" : "paused"
                  }}
                >
                  <text
                    fill={isHighlighted ? "#ffffff" : ring.textColor}
                    fontSize={ring.fontSize}
                    fontFamily="monospace"
                    fontWeight={ring.fontWeight}
                    letterSpacing={ring.letterSpacing}
                    className="select-none transition-colors duration-200"
                    textLength={circumference}
                    lengthAdjust="spacing"
                  >
                    <textPath
                      href={`#curve-path-${ring.id}`}
                      startOffset="0%"
                    >
                      {fullToolsString}
                    </textPath>
                  </text>
                </g>
              </g>
            );
          })}

          {/* =========================================================================
              3. CENTER RADAR CORE MEDALLION
             ========================================================================= */}
          {showCenterHub && (
            <>
              {/* Outer Saddle Ring */}
              <circle
                cx={CX}
                cy={CY}
                r={R_CORE + 6}
                fill="none"
                stroke="#d8aa68"
                strokeWidth="1.5"
                opacity="0.8"
              />

              {/* Center Medallion Group (Click to pause / resume continuous orbit) */}
              <g 
                className="cursor-pointer group"
                onClick={() => setIsRotating(!isRotating)}
              >
                <title>{isRotating ? "Click to pause rotation" : "Click to resume rotation"}</title>
                
                {/* Center Medallion Body Background */}
                <circle
                  cx={CX}
                  cy={CY}
                  r={R_CORE}
                  fill="#120501"
                  stroke="#d8aa68"
                  strokeWidth="3"
                  className="drop-shadow-[0_8px_32px_rgba(0,0,0,0.98)] group-hover:stroke-[#ffe0a3] transition-colors"
                />

                {/* Embedded Circular Pelican Logo in Center Hub */}
                <image
                  href="/brand_logo.png?v=4"
                  x={CX - (R_CORE - 4)}
                  y={CY - (R_CORE - 4)}
                  width={(R_CORE - 4) * 2}
                  height={(R_CORE - 4) * 2}
                  preserveAspectRatio="xMidYMid meet"
                  clipPath="url(#centerLogoClip)"
                  className="transition-all duration-300 pointer-events-none select-none"
                  style={{
                    opacity: activeHoveredRing ? 0.32 : 1,
                    filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.9))"
                  }}
                />

                {/* Inner Golden Rim & Bezel Accent */}
                <circle
                  cx={CX}
                  cy={CY}
                  r={R_CORE - 4}
                  fill="none"
                  stroke="#ffe0a3"
                  strokeWidth="1.5"
                  strokeOpacity="0.55"
                  className="pointer-events-none"
                />

                {/* Center Hub Dynamic Content via ForeignObject (shows when a ring is hovered) */}
                {activeHoveredRing && (
                  <foreignObject
                    x={CX - R_CORE + 6}
                    y={CY - R_CORE + 6}
                    width={(R_CORE - 6) * 2}
                    height={(R_CORE - 6) * 2}
                    className="pointer-events-none select-none"
                  >
                    <div className="w-full h-full flex flex-col items-center justify-center text-center p-2 rounded-full bg-[#120501]/85 backdrop-blur-[2px] border border-[#d8aa68]/40 shadow-inner">
                      <div className="w-6 h-6 rounded-full bg-[#381b0e] border border-[#d8aa68] flex items-center justify-center text-[#ffe0a3] mb-1 shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
                        <activeHoveredRing.icon size={13} strokeWidth={2.5} />
                      </div>

                      <span className="text-[8.5px] font-mono text-[#d8aa68] uppercase tracking-[0.22em] font-bold">
                        Ring {activeHoveredRing.number}
                      </span>

                      <div className="text-[11px] font-display font-black text-white uppercase tracking-tight line-clamp-1">
                        {activeHoveredRing.title.split("&")[0]}
                      </div>

                      <div className="w-8 h-[1px] bg-gradient-to-r from-transparent via-[#d8aa68] to-transparent my-1" />

                      <span className="text-[9px] font-mono text-[#ffe0a3] font-semibold tracking-wider">
                        {activeHoveredRing.tools.length} Tools In Orbit
                      </span>
                    </div>
                  </foreignObject>
                )}
              </g>
            </>
          )}

        </svg>
      </div>

      {/* Bottom Information Legend Bar */}
      {showCenterHub && (
        <div className="w-full mt-6 flex flex-wrap items-center justify-center gap-3 max-w-4xl px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1b0d06]/80 border border-[#d8aa68]/30 text-xs font-mono text-[#f5ebe0] shadow-[0_4px_16px_rgba(0,0,0,0.6)]">
            <RotateCw size={13} className="text-[#d8aa68] animate-spin" style={{ animationDuration: "12s" }} />
            <span>All 110+ tools are actively curved along 6 concentric orbital lines rotating at synchronized horology speeds. Hover any ring to inspect.</span>
          </div>
        </div>
      )}

    </div>
  );
};
